import React, { useCallback, useMemo } from 'react';
import {
  useGeckoIntegrations,
  useGeckoIntegrationsFunction,
} from 'services/gecko-integrations';
import GeckoAuth from 'services/gecko-auth';
import {
  GECKO_CONSTS,
  GeckoCardTable,
  GeckoDialogService,
  GeckoModalService,
} from 'ui';
import Loader from 'components/Loader';
import GeckoErrorsAlert from 'components/GeckoErrorsAlert';
import MappingModal from './MappingModal';
import RelationshipModal from './RelationshipModal';
import EllucianIntegration from './Ellucian';

const { INTEGRATION } = require('chat-constants');

const Integration = ({ integrationId }) => {
  const GeckoIntegrations = useGeckoIntegrations({
    getAuthTokenCallback: GeckoAuth.getToken,
  });

  const getIntegration = useCallback(() => {
    return GeckoIntegrations.getIntegration(integrationId);
  }, [GeckoIntegrations, integrationId]);

  const {
    data: integration,
    isLoading: isLoadingFieldMappings,
    errors: fieldMappingErrors,
    reload: reloadFieldMappings,
  } = useGeckoIntegrationsFunction(getIntegration, { initialValue: [] });

  const getIntegrations = useCallback(() => {
    return GeckoIntegrations.getIntegrations();
  }, [GeckoIntegrations]);
  const {
    data: integrations,
    isLoading: isLoadingIntegrations,
    errors: integrationsErrors,
  } = useGeckoIntegrationsFunction(getIntegrations, { initialValue: [] });

  const createModuleRelationship = (availableModuleRelationship) => {
    availableModuleRelationship.relationshipAttributeId = availableModuleRelationship.attributeId
    return GeckoIntegrations.addModuleRelationship(integrationId, availableModuleRelationship, true)
  }

  const deleteModuleRelationship = (moduleRelationship) => {
    const {moduleRelationshipId} = moduleRelationship
    return GeckoIntegrations.deleteModuleRelationship(integrationId, moduleRelationshipId)
  }

  const { primaryConnectionType, secondaryConnectionType } = useMemo(() => {
    const integration = integrations.find(
      (integration) => integration.integrationId === integrationId
    );
    if (!integration) {
      return {
        primaryConnectionType: 'Primary',
        secondaryConnectionType: 'Secondary',
      };
    }
    return {
      primaryConnectionType: integration.primaryConnectionConfig.connectionType,
      secondaryConnectionType:
        integration.secondaryConnectionConfig.connectionType,
    };
  }, [integrations, integrationId]);

  const getUpdateMatchingConditionsBody = (integration, mappedField) => {
    let newMatchingConditions = null;
    const module = integration.modules.find(
      (mod) =>
        mod.handle === mappedField.secondaryAttribute.module &&
        mod.connectionCategory === 'secondary'
    );

    const matchingConditions = module.config.matchingConditions || [];
    if (mappedField.usedForMatchingConditions === 'No') {
      if (matchingConditions.length === 0) {
        newMatchingConditions = [
          {
            priority: 1,
            attributes: [mappedField.secondaryAttribute.id],
            sourceModuleHandle: mappedField.primaryAttribute.module,
          },
        ];
      } else {
        newMatchingConditions = [
          Object.assign(matchingConditions[0], {
            attributes: matchingConditions[0].attributes.concat([
              mappedField.secondaryAttribute.id,
            ]),
          }),
        ];
      }
    } else {
      const updatedAttributes = matchingConditions[0].attributes.filter(
        (attr) => attr !== mappedField.secondaryAttribute.id
      );

      if (updatedAttributes.length === 0) {
        newMatchingConditions = [];
      } else {
        newMatchingConditions = [
          Object.assign(matchingConditions[0], {
            attributes: updatedAttributes,
          }),
        ];
      }
    }

    return {
      connectionCategory: 'secondary',
      matchingConditions: newMatchingConditions,
      moduleHandle: mappedField.secondaryAttribute.module,
      moduleId: module.moduleId,
    };
  };

  const getUsedInMatchingConditions = (mappedField, modules) => {
    const module =
      modules.find(
        (module) =>
          module.handle === mappedField.secondaryAttribute.module &&
          module.connectionCategory === 'secondary'
      ) || {};

    return (module.config.matchingConditions || []).find((condition) =>
      condition.attributes.includes(mappedField.secondaryAttribute.id)
    )
      ? 'Yes'
      : 'No';
  };

  const formatMappedFields = (integration) => {
    const mappedFields = integration.mappedFields || [];
    const modules = integration.modules || [];
    return mappedFields.reduce(
      ({ contacts, conversations }, mappedField) => {
        if (mappedField.primaryAttribute.module === 'conversation') {
          conversations.push(mappedField);
        } else {
          contacts.push({
            ...mappedField,
            usedForMatchingConditions: getUsedInMatchingConditions(
              mappedField,
              modules
            ),
          });
        }
        return { contacts, conversations };
      },
      { contacts: [], conversations: [] }
    );
  };

  const { conversations: conversationFields, contacts: contactFields } =
    formatMappedFields(integration);

  const permittedSecondaryModuleHandles = useMemo(() => {
    const permitted = {
      [INTEGRATION.MODULE_HANDLE_CHAT_CONTACT]: [],
      [INTEGRATION.MODULE_HANDLE_CHAT_CONVERSATION]: [],
    };
    if (secondaryConnectionType === INTEGRATION.SALESFORCE) {
      const contactHandle = contactFields[0]?.secondaryAttribute?.module;
      const conversationHandle =
        conversationFields[0]?.secondaryAttribute?.module;
      permitted[INTEGRATION.MODULE_HANDLE_CHAT_CONTACT] = contactHandle
        ? [contactHandle]
        : [INTEGRATION.MODULE_HANDLE_SALESFORCE_CONTACT, INTEGRATION.MODULE_HANDLE_SALESFORCE_LEAD].filter(
            (module) => module !== conversationHandle
          );
      permitted[INTEGRATION.MODULE_HANDLE_CHAT_CONVERSATION] = conversationHandle
        ? [conversationHandle]
        : [];
    }
    return permitted;
  }, [secondaryConnectionType, contactFields, conversationFields]);

  const excludedSecondaryModuleHandles = useMemo(() => {
    const excluded = {
      [INTEGRATION.MODULE_HANDLE_CHAT_CONTACT]: [],
      [INTEGRATION.MODULE_HANDLE_CHAT_CONVERSATION]: [],
    };
    if (secondaryConnectionType === INTEGRATION.SALESFORCE) {
      excluded[INTEGRATION.MODULE_HANDLE_CHAT_CONTACT] = [
        conversationFields[0]?.secondaryAttribute?.module,
      ];
      excluded[INTEGRATION.MODULE_HANDLE_CHAT_CONVERSATION] = [
        contactFields[0]?.secondaryAttribute?.module,
      ];
    }
    return excluded;
  }, [secondaryConnectionType, contactFields, conversationFields]);

  const reloadWithChildEntityCheck = (predicate, entityCollectionKey, entityIdProperty, entityId, maxRetries = 5) => {
    let count = 0;
    const reload = () => {
      reloadFieldMappings().then((integration) => {
        if (count < maxRetries && predicate(integration, entityCollectionKey, entityIdProperty, entityId)) {
          setTimeout(() => {
          }, 500);
          count++;
          reload();
        }
        return false
      })
    }

    reload();
  };

  const integrationHasChildEntity = (integration, entityCollectionKey, entityIdProperty, entityId) => {
    return integration[entityCollectionKey].findIndex(entity => entity[entityIdProperty] === entityId) !== -1
  }

  const integrationDoesNotHaveChildEntity = (integration, entityCollectionKey, entityIdProperty, entityId) => {
    return !integrationHasChildEntity(integration, entityCollectionKey, entityIdProperty, entityId)
  }

  const hasActiveRelationship = () =>
    integration.moduleRelationships?.length > 0;
  const hasPotentialRelationship = () =>
    integration.moduleRelationships?.length > 0 ||
    integration.availableModuleRelationships?.length > 0;

  const openRelationshipModal = () => {
    const relationships = [
      ...integration.moduleRelationships.map(
        (item) => Object.assign(item, {
          attributeId: item.relationshipAttributeId,
          attributeLabel: item.relationshipAttributeLabel
        })
      ),
      ...integration.availableModuleRelationships,
    ].filter(
      ({ sourceCategory, sourceModuleLabel }) =>
        sourceCategory === INTEGRATION.CONNECTION_CATEGORY_PRIMARY &&
        sourceModuleLabel === INTEGRATION.MODULE_LABEL_CHAT_CONVERSATION
    );

    return new GeckoModalService().open(() => (
      <RelationshipModal
        destinationModuleLabel={relationships[0].destinationModuleLabel}
        relatedModuleLabel={relationships[0].relatedModuleLabel}
        submitRelationship={(availableModuleRelationship) => createModuleRelationship(availableModuleRelationship)}
        salesforceFields={relationships}
        isEdit={hasActiveRelationship()}
        deleteRelationship={(moduleRelationship) => deleteModuleRelationship(moduleRelationship)}
      />
    )).then((result)=> {
      const {moduleRelationshipId} = result
      const predicate = result.method === 'delete' ? integrationHasChildEntity: integrationDoesNotHaveChildEntity
      reloadWithChildEntityCheck(
        predicate,
        'moduleRelationships',
        'moduleRelationshipId',
        moduleRelationshipId
      )
    })
  };

  const openMappingModal = useCallback(
    (primaryModule, mappedField = null) => {
      GeckoIntegrations.getPrimaryAttributes(integrationId, primaryModule).then(
        (primaryAttributes) => {
          return new GeckoModalService()
            .open(() => {
              return (
                <MappingModal
                  permittedSecondaryModuleHandles={
                    permittedSecondaryModuleHandles
                  }
                  excludedSecondaryModuleHandles={
                    excludedSecondaryModuleHandles
                  }
                  mappedField={mappedField}
                  integrationId={integrationId}
                  primaryConnectionType={primaryConnectionType}
                  secondaryConnectionType={secondaryConnectionType}
                  primaryAttributes={primaryAttributes.data || []}
                  primaryModule={primaryModule}
                />
              );
            })
            .then((response) => {
              if (!response) {
                reloadFieldMappings()
                return
              }
              const {fieldId} = response
              reloadWithChildEntityCheck(integrationDoesNotHaveChildEntity, 'mappedFields', 'fieldId', fieldId)
            });
        }
      );
    },
    [
      reloadFieldMappings,
      integrationId,
      primaryConnectionType,
      secondaryConnectionType,
      GeckoIntegrations,
      permittedSecondaryModuleHandles,
      excludedSecondaryModuleHandles,
    ]
  );

  const cantBeUsedForMatchingConditions = (mappedField) => {
    const forbiddenTypes = ['textarea', 'encryptedstring', 'currency'];

    return forbiddenTypes.find(
      (type) => mappedField.secondaryAttribute.type === type
    );
  };

  const matchingConditionToggle = (mappedField) => {
    const getUpdateMatchingConditions = (mappedField) => {
      return new GeckoDialogService()
        .confirm(
          `Are you sure you want to toggle the matching conditions usage for this mapped field?`
        )
        .then((response) => {
          if (response) {
            return GeckoIntegrations.updateMatchingConditions(
              integrationId,
              getUpdateMatchingConditionsBody(integration, mappedField)
            ).then(reloadFieldMappings);
          }
          return false;
        });
    };

    return getUpdateMatchingConditions(mappedField);
  };

  const removeHandler = (mappedField) => {
    return new GeckoDialogService()
      .confirm(`Are you sure you want to delete this field mapping?`)
      .then((response) => {
        if (response) {
          return GeckoIntegrations.deleteFieldMapping(
            integrationId,
            mappedField.fieldId
          ).then(() => {
            reloadWithChildEntityCheck(integrationHasChildEntity, 'mappedFields', 'fieldId', mappedField.fieldId);
          });
        }
        return false;
      });
  };

  const columns = useMemo(() => {
    const integrationModuleColumn = {
      key: 'secondaryAttribute.module',
      name: `${secondaryConnectionType} module`,
      sortable: true,
    };
    const integrationLabelColumn = {
      key: 'secondaryAttribute.label',
      name: `${secondaryConnectionType} field`,
      sortable: true,
    };
    const chatLabelColumn = {
      key: 'primaryAttribute.label',
      name: `${primaryConnectionType} field`,
      sortable: true,
    };
    const matchingConditionsColumn = {
      key: 'usedForMatchingConditions',
      name: 'Used for matching conditions',
      width: '10%',
      sortable: true,
    };
    const dropDownColumn = (primaryModule) => ({
      type: GECKO_CONSTS.TABLE_CELL_TYPE_DROPDOWN,
      width: '1%',
      items: [
        {
          name: 'Use in Matching Conditions',
          hideWhen: (mappedField) =>
            cantBeUsedForMatchingConditions(mappedField) ||
            primaryModule === INTEGRATION.MODULE_HANDLE_CHAT_CONVERSATION,
          handler: matchingConditionToggle,
        },
        {
          name: 'Edit',
          handler: (mappedField) => {
            return openMappingModal(primaryModule, mappedField);
          },
        },
        {
          name: 'Remove',
          handler: removeHandler,
        },
      ],
    });

    return {
      contact: [
        { ...integrationModuleColumn, width: '30%' },
        { ...integrationLabelColumn, width: '30%' },
        { ...chatLabelColumn, width: '30%' },
        { ...matchingConditionsColumn, width: '9%' },
        { ...dropDownColumn(INTEGRATION.MODULE_HANDLE_CHAT_CONTACT), width: '1%' },
      ],
      conversation: [
        { ...integrationModuleColumn, width: '33%' },
        { ...integrationLabelColumn, width: '33%' },
        { ...chatLabelColumn, width: '33%' },
        { ...dropDownColumn(INTEGRATION.MODULE_HANDLE_CHAT_CONVERSATION), width: '1%' },
      ],
    };
  }, [
    integration,
    primaryConnectionType,
    secondaryConnectionType,
    GeckoIntegrations,
    integrationId,
    reloadFieldMappings,
    openMappingModal,
  ]);

  const contactHeaderButtons = useMemo(() => {
    return [
      {
        name: 'Add new contact field',
        handler: () => openMappingModal(INTEGRATION.MODULE_HANDLE_CHAT_CONTACT),
        type: GECKO_CONSTS.BTN_TYPE_PRIMARY,
      },
    ];
  }, [openMappingModal]);

  const conversationHeaderButtons = useMemo(() => {
    return [
      {
        name: hasActiveRelationship()
          ? 'Edit relationship'
          : 'Add relationship',
        handler: () => openRelationshipModal(),
        type: GECKO_CONSTS.BTN_TYPE_DEFAULT,
        hideWhen: () => !hasPotentialRelationship(),
      },
      {
        name: 'Add new conversation field',
        handler: () =>
          openMappingModal(INTEGRATION.MODULE_HANDLE_CHAT_CONVERSATION),
        type: GECKO_CONSTS.BTN_TYPE_PRIMARY,
      },
    ];
  }, [openMappingModal, integration]);

  const errors = useMemo(() => {
    return [...fieldMappingErrors, ...integrationsErrors];
  }, [fieldMappingErrors, integrationsErrors]);

  const openHelpLink = (link) => () => window.open(link);
  const renderHelpLinkIcon = (link) => <i
    className="fas fa-question-circle Integration-info-button"
    onClick={openHelpLink(link)}
  />

  const render = (
    <div className="content-margin Integrations">
      <Loader isLoading={isLoadingFieldMappings || isLoadingIntegrations}>
        <GeckoErrorsAlert errors={errors} />
        <GeckoCardTable
          name={<span>
            Contact fields {renderHelpLinkIcon(INTEGRATION.GECKO_ACADEMY_INTEGRATING_CONTACTS_URL)}
          </span>}
          cols={columns.contact}
          rowHandler={(mappedField) =>
            openMappingModal(INTEGRATION.MODULE_HANDLE_CHAT_CONTACT, mappedField)
          }
          rows={contactFields}
          headerBtns={contactHeaderButtons}
          newLinkHandler={() => openMappingModal(INTEGRATION.MODULE_HANDLE_CHAT_CONTACT)}
          alert="You have not mapped any contact fields yet."
        />
        <GeckoCardTable
          name={<span>
            Conversation fields {renderHelpLinkIcon(INTEGRATION.GECKO_ACADEMY_INTEGRATING_CONVERSATIONS_URL)}
          </span>}
          cols={columns.conversation}
          rowHandler={(mappedField) =>
            openMappingModal(INTEGRATION.MODULE_HANDLE_CHAT_CONVERSATION, mappedField)
          }
          rows={conversationFields}
          headerBtns={conversationHeaderButtons}
          newLinkHandler={() => openMappingModal(INTEGRATION.MODULE_HANDLE_CHAT_CONVERSATION)}
          alert="You have not mapped any conversation fields yet."
        />
      </Loader>
    </div>
  );

  return secondaryConnectionType === 'Ellucian' ? <EllucianIntegration /> : render;
};

export default Integration;
